/**
 * Per-tab impersonation bootstrap. Runs at import time, BEFORE the auth store is
 * constructed, so the store's persist storage selector can choose sessionStorage
 * for an impersonation tab.
 *
 * Flow: a parent admin opens `/login/impersonate#data=<urlenc base64 JSON>` in a
 * NEW tab. The hash carries `{ t: accessToken, u: user }`. We:
 *   1. mark this tab as an impersonation tab (`b1fr0st.impersonation` flag),
 *   2. seed the zustand-persist payload into sessionStorage under `b1fr0st.auth`
 *      (NOT localStorage — that is shared across tabs and would clobber the
 *      parent admin's own session),
 *   3. strip the token from the URL so it never lands in history / referrer.
 *
 * Everything is wrapped in try/catch — a malformed hash must never crash boot.
 */
const IMPERSONATION_FLAG = 'b1fr0st.impersonation';
const AUTH_STORAGE_KEY = 'b1fr0st.auth';

/** True when this tab was seeded by a parent admin's impersonation hand-off. */
export function isImpersonationTab(): boolean {
  return (
    typeof sessionStorage !== 'undefined' &&
    sessionStorage.getItem(IMPERSONATION_FLAG) === '1'
  );
}

try {
  if (
    typeof window !== 'undefined' &&
    window.location.pathname === '/login/impersonate' &&
    window.location.hash.length > 1
  ) {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const raw = params.get('data');
    if (raw) {
      const decoded = JSON.parse(atob(decodeURIComponent(raw))) as {
        t?: string;
        u?: unknown;
      };
      if (decoded.t && decoded.u) {
        sessionStorage.setItem(IMPERSONATION_FLAG, '1');
        sessionStorage.setItem(
          AUTH_STORAGE_KEY,
          JSON.stringify({
            state: {
              accessToken: decoded.t,
              refreshToken: '',
              user: decoded.u,
              isAuthenticated: true,
            },
            version: 0,
          }),
        );
        window.history.replaceState(null, '', '/');
      }
    }
  }
} catch {
  // Never throw at boot — a bad hash just means no impersonation session.
}
